/**
 * Legacy view-URL migration utilities.
 *
 * These helpers convert saved-view URLs that were generated by the pre-redesign
 * application into the URL format expected by the current routing layer.
 *
 * Extracted from `src/hooks/useURLSync/oldUtils.ts` (deprecated) so that
 * `ViewCardFooter.tsx` no longer depends on the deprecated hook utilities
 * directory.
 *
 * @see src/pages/Report/components/ViewCardFooter/ViewCardFooter.tsx
 *
 * DO NOT add new usages. Once saved-view URLs are migrated to the new format
 * on the backend, this file can be deleted.
 */

import {
  BRANDS,
  BUTTERLFY,
  DIFFERENTIATION,
  FILTERS,
  MARKETS,
  PERCEPTUAL,
  POINT_IN_TIME,
  DISTRIBUTION,
  QUESTION_OPTIONS,
  REPUTATION,
  SECONDARY_BRANDS,
  SEGMENTS,
  TREND,
  VISIBILITY,
  LINE,
  BAR,
  QUESTIONS,
  EQUITY,
  DONUT,
  ADVERTISING,
  CONVERSION,
  FUNNEL,
  EMOTIONAL,
  BUBBLE,
  BARCODE,
  FILTERSV2,
} from "@/constant/constantsThrougoutProject";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import type { MarketAdapterType } from "@/store/slices/markets/marketsSlice";
import { DateTime } from "luxon";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const PERIOD_KEYS: Record<string, string> = {
  "P-W": "P_W",
  "P-M": "P_M",
  "P-Q": "P_Q",
  "MV-1": "MV_1",
  "MV-2": "MV_2",
  "MV-4": "MV_4",
  "MV-8": "MV_8",
  "MV-12": "MV_12",
};

const POINT_IN_TIME_VIZ_BY_MODULE: Readonly<Record<string, string>> = {
  [EQUITY]: DONUT,
  [ADVERTISING]: DONUT,
  [REPUTATION]: DONUT,
  [CONVERSION]: FUNNEL,
  [EMOTIONAL]: BUBBLE,
  [VISIBILITY]: BARCODE,
  [QUESTIONS]: BAR,
};

export function parseOldURLParams(queryString: URLSearchParams) {
  const result: Record<string, string | number | string[] | number[] | (string | number)[]> = {};
  for (const [key] of queryString.entries()) {
    if (key === "questionOptions" || key === "market" || key === "markets") {
      result[key] = queryString.getAll(key);
    } else if (key === "brands" || key === "brand") {
      // Brand IDs can be numeric (regular brands) or ULID strings (brand sets)
      result[key] = queryString.getAll(key).map((val) => {
        const num = Number(val);
        return isNaN(num) ? val : num;
      });
    } else if (key === "secondaryBrands") {
      // Brand IDs can be numeric (regular brands) or ULID strings (brand sets)
      result[key] = queryString.getAll(key).map((val) => {
        const num = Number(val);
        return isNaN(num) ? val : num;
      });
    } else if (key === "segments" || key === FILTERSV2) {
      result[key] = queryString.getAll(key);
    } else {
      result[key] = queryString.get(key) as string;
    }
  }
  return result;
}

function getDefaultVizTypeBasedOnModule(module: string, mode: string): string {
  if (mode === TREND) return LINE;
  return POINT_IN_TIME_VIZ_BY_MODULE[module] ?? BAR;
}


// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Convert a legacy saved-view URL string (`view.viewUrl`) into the
 * `{ pathname, url }` pair expected by `ViewCardFooter`.
 *
 * @param url - The raw URL query string stored in the saved view record.
 * @param markets - The current market entity adapter from Redux state.
 */
export function getNewURL(
  url: string,
  markets: MarketAdapterType,
): { pathname: string; url: string } {
  const result = parseOldURLParams(new URLSearchParams(url?.split('?')[1]));
  if (!result?.visualizationType) {
    result["visualizationType"] = getDefaultVizTypeBasedOnModule(result?.module as string,result.mode as string)
  }
  const pathName: string =  url?.split('?')?.[0]
  
  const finalParams: any = {};
  Object.entries(result)?.forEach(([key, value]: any[]) => {
    if (key === "base" || key === "audienceType") {
      finalParams["base"] = value;
    } else if (key === "statTest") {
      if (value === "POP" || value === "PTP" || value === "YOY" || value === "NONE") {
        finalParams["statTest"] = value;
      } else if (value === "true" || value === true) {
        finalParams["statTest"] = "POP";
      } else {
        finalParams["statTest"] = "NONE";
      }
    } else if (key === "periodType" || key == "postPeriodType") {
      finalParams[key] = value;
    } else if (
      key === "periodStartDate" ||
      key === "periodEndDate" ||
      key === "postStartDate" ||
      key === "postEndDate"
    ) {
      if (value?.length === 10) {
        finalParams[key] = value;
      }
    } else if (key === "intervalType") {
      finalParams["period"] = PERIOD_KEYS[value] || value;
    } else if (key === "market" || key === "markets") {
      const marketIds = value
        ?.map((val: string) => {
          const market = markets?.ids?.find((marketId: number) => {
            if (marketId === Number(val)) {
              return true;
            }
            return false;
          });
          if (market) {
            return market;
          }
          return null;
        })
        .filter((val: any) => val !== null);
      finalParams["markets"] = marketIds;
    } else if (key === "visualizationType") {
      finalParams["visualizationType"] = result["visualizationType"];
    } else if (key === "brand" || key === "brands" ) {
      if (result?.visualizationType === DIFFERENTIATION) {
        if((key === "brand" || key === "brands"))finalParams["brands"] = value?.slice(0, 1);
      } else if (result?.visualizationType === PERCEPTUAL) {
        finalParams["brands"] = value;
      } else if (result?.visualizationType === BUTTERLFY) {
        if((key === "brand" || key === "brands"))finalParams["brands"] = value?.slice(0, 1);
      } else {
        finalParams["brands"] = value;
      }
    }else if(key === "secondaryBrands"){
      if (result?.visualizationType === DIFFERENTIATION) {
        if(key === "secondaryBrands") finalParams["secondaryBrands"] = value;
      } else if (result?.visualizationType === BUTTERLFY) {
        if(key === "secondaryBrands") finalParams["secondaryBrands"] = value;
      }
    } else if (
      key === "segments" ||
      key === FILTERSV2 ||
      key === "questionOptions"
    ) {
      finalParams[key] = value;
    } else if (key === "metric" && result?.mode === "TREND") {
      finalParams["trendsMetric"] = value?.toLowerCase()?.replace(/[^a-z0-9]/g, "");
    } else if ((key === "response" && result?.mode === "TREND") || (key === "response" && result?.scoreType === "PERCENTAGE")) {
      finalParams[key] = value;
    } else if (key === "viewId" || key === "groupedBy") {
      finalParams[key] = value;
    } else if (key === "profileGroup" && result?.visualizationType === "DISTRIBUTION") {
      finalParams[key] = value;
    } else if (key === "profileMetric" && result?.visualizationType === "DISTRIBUTION") {
      finalParams[key] = value;
    } else if (key === "profileResponse" && result?.visualizationType === "DISTRIBUTION") {
      finalParams[key] = value;
    } else if (key === "scoreType") {
      finalParams["scoreType"] = value;
    }
  });
  finalParams["mode"] = result["mode"];

  const currentPathConfig = ROUTES_CHILDREN_LOOKUP[pathName];
  let mode: string =
    typeof result?.mode === "string"
      ? result?.mode
      : "POINT_IN_TIME";
  if (
    mode === POINT_IN_TIME &&
    (result?.visualizationType === PERCEPTUAL ||
      result?.visualizationType === BUTTERLFY ||
      result?.visualizationType === DIFFERENTIATION ||
      result?.visualizationType === DISTRIBUTION)
  ) {
    mode = result.visualizationType;
  }
  const urlParams = new URLSearchParams();
  currentPathConfig?.urlKeysMapping?.[
    (currentPathConfig?.modeOptions?.includes(mode) && mode) || "POINT_IN_TIME"
  ]?.forEach((key: string) => {
    if (
      key === MARKETS ||
      key === BRANDS ||
      key === FILTERSV2 ||
      key === SECONDARY_BRANDS ||
      key === SEGMENTS ||
      key === QUESTION_OPTIONS
    ) {
      console.log(key,finalParams,"finalParams",result)
      if (finalParams[key]?.length > 0) {
        finalParams[key]?.map((val: any) => urlParams.append(key, val));
      }
            console.log(key,finalParams,"finalParams",result)

    } else if (finalParams[key]) {
      urlParams.append(key, finalParams[key]);
    }
    console.log(urlParams?.toString(),"urlParams?.toString()",result,finalParams)
  });
  return { pathname: pathName, url: urlParams?.toString() };
}